import React, { useEffect, useMemo, useState } from 'react';
import styles from '../insertsPages.module.css';
import Tabs from '../../../Tabs';
import Chooser from '../../../Chooser/index.jsx';
import { useUserInput } from '../../../../context/UserInputContext.tsx';
import { Visualiser, VisualiserTextBox, VisualiserGroup } from '../../../Visualiser/index';
import { getInsertsAndCardsProduct } from '../../../../utilities/visualiserVariants.js';

const tabConfig = [
  { id: 'FRONT', label: 'Front' },
  { id: 'INSIDE', label: 'Inside' },
  { id: 'BACK', label: 'Back' }
];

/**
 * CardThemedCustomMessage component allows users to select a themed design and add a message for their greeting card.
 *
 * @param {Object} props - Component props.
 * @returns {import('react').JSX.Element}
 */
export function CardThemedCustomMessage({ ...rest }) {
  const { getTextInput, setTextInput, setVisualiserSelection, clearVisualiserSelection } =
    useUserInput();

  const [activeIndex, setActiveIndex] = useState(0);

  const themedProduct = useMemo(() => {
    return getInsertsAndCardsProduct({ productKey: 'themedGreetingsCards' });
  }, []);

  const textVariants = useMemo(() => {
    if (!themedProduct || !Array.isArray(themedProduct.variants)) return [];
    return themedProduct.variants.filter((variant) => variant?.option1 === 'Text');
  }, [themedProduct]);

  const [selectedVariantId, setSelectedVariantId] = useState(
    textVariants && textVariants.length > 0 ? textVariants[0].id : null
  );

  useEffect(() => {
    if (!textVariants || textVariants.length === 0) return;
    if (!selectedVariantId || !textVariants.find((variant) => variant.id === selectedVariantId)) {
      setSelectedVariantId(textVariants[0].id);
    }
  }, [textVariants, selectedVariantId]);

  const selectedVariant = useMemo(
    () => textVariants.find((variant) => variant.id === selectedVariantId) || null,
    [textVariants, selectedVariantId]
  );

  const backgroundImageUrl = useMemo(() => {
    const raw = selectedVariant?.featured_image?.src || selectedVariant?.featured_media?.preview_image?.src || null;
    if (!raw || typeof raw !== 'string') return null;
    return raw.startsWith('/' + '/') ? `https:${raw}` : raw;
  }, [selectedVariant]);

  useEffect(() => {
    const productKey = 'themedGreetingsCards';
    setVisualiserSelection({
      productKey,
      variant: selectedVariant,
      config: {
        templateId: 'CARD_THEMED_CUSTOM_MESSAGE',
        mapping: {
          text: {
            'cardThemedCustomMessage.inside': 'UVInsideText'
          }
        }
      }
    });
  }, [selectedVariant, setVisualiserSelection]);

  useEffect(() => {
    return () => {
      clearVisualiserSelection();
    };
  }, [clearVisualiserSelection]);

  return (
    <div className={styles['visualiser-stack']}>
      <div className={styles['page--container']}>
        <Tabs tabs={tabConfig} activeIndex={activeIndex} onChange={setActiveIndex} fullWidth={true} />

        {activeIndex === 0 && (
          <Visualiser type="GREETINGS_CARD" background={backgroundImageUrl ? `url(${backgroundImageUrl})` : 'var(--color-neutral)'} subtitle="Not editable">
            {' '}
          </Visualiser>
        )}

        {activeIndex === 1 && (
          <VisualiserGroup>
            <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Not editable">
              {' '}
            </Visualiser>
            <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Tap to edit">
              <VisualiserTextBox
                size={{
                  width: '190px',
                  minWidth: '190px',
                  maxWidth: '190px',
                  minHeight: '50px',
                  maxHeight: '260px'
                }}
                position={{
                  top: '50%',
                  left: '50%'
                }}
                maxLength={150}
                maxLines={12}
                lineHeight={1.2}
                fontSize={16}
                style={{
                  transform: 'translate(-50%, -50%)'
                }}
                value={getTextInput('cardThemedCustomMessage.inside')}
                setValue={((value) => setTextInput('cardThemedCustomMessage.inside', value))}
              />
            </Visualiser>
          </VisualiserGroup>
        )}

        {activeIndex === 2 && (
          <Visualiser type="GREETINGS_CARD" background="var(--color-white)" subtitle="Not editable">
            {' '}
          </Visualiser>
        )}
      </div>

      {textVariants.length > 0 && (
        <Chooser
          title="Choose a themed design"
          subtitle={
            selectedVariant
              ? `${selectedVariant.public_title || selectedVariant.title || 'Design selected'}`.replace('Text / ', '')
              : 'Select a design'
          }
        >
          <div className={styles['chooser--content']}>
            <div className={styles['stock-card--chooser-grid']}>
              {textVariants.map((variant) => {
                const variantImg =
                  (variant?.featured_image && variant.featured_image.src) ||
                  variant?.featured_media?.preview_image?.src ||
                  null;
                const variantImgUrl =
                  variantImg && typeof variantImg === 'string'
                    ? variantImg.startsWith('/' + '/')
                      ? `https:${variantImg}`
                      : variantImg
                    : null;
                return (
                  <button
                    key={variant.id}
                    type="button"
                    className={`${styles['stock-card--chooser-item']} ${
                      selectedVariantId === variant.id ? styles['stock-card--chooser-item--selected'] : ''
                    }`}
                    onClick={() => setSelectedVariantId(variant.id)}
                  >
                    {variantImgUrl ? <img src={variantImgUrl} alt={variant.title || 'Card design'} /> : null}
                    <p>{`${variant.public_title || variant.title || 'Card design'}`.replace('Text / ', '')}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </Chooser>
      )}
    </div>
  );
}
